/* eslint-disable react/no-unknown-property */
import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { MAT, PALETTE } from "./studio";

function useHover(hoverRef) {
  return hoverRef;
}

// ---- Individual model builders (composed from primitives) ----

function Gem() {
  return (
    <group>
      <mesh rotation={[0, 0.4, 0]}>
        <octahedronGeometry args={[0.95, 0]} />
        <meshStandardMaterial {...MAT.chrome} />
      </mesh>
      <mesh scale={1.02} rotation={[0, 0.4, 0]}>
        <octahedronGeometry args={[0.95, 0]} />
        <meshStandardMaterial color={PALETTE.violet} emissive={PALETTE.violet} emissiveIntensity={0.5} wireframe transparent opacity={0.5} />
      </mesh>
    </group>
  );
}

function Easel() {
  const cols = [MAT.accent, MAT.pink, MAT.coral];
  return (
    <group rotation={[0.1, 0, 0]}>
      {[-0.5, 0, 0.5].map((x, i) => (
        <mesh key={i} position={[x, Math.sin(i) * 0.1, 0]} rotation={[0, (i - 1) * 0.5, 0]}>
          <boxGeometry args={[0.42, 1.1, 0.05]} />
          <meshStandardMaterial {...cols[i]} emissiveIntensity={0.4} />
        </mesh>
      ))}
    </group>
  );
}

function Target() {
  const orbit = useRef();
  useFrame((s) => {
    if (orbit.current) orbit.current.rotation.z = s.clock.elapsedTime * 0.6;
  });
  return (
    <group rotation={[Math.PI / 2.2, 0, 0]}>
      {[1.0, 0.68, 0.36].map((r, i) => (
        <mesh key={i}>
          <torusGeometry args={[r, 0.06, 16, 48]} />
          <meshStandardMaterial {...(i % 2 ? MAT.chrome : MAT.accent)} />
        </mesh>
      ))}
      <mesh>
        <sphereGeometry args={[0.14, 24, 24]} />
        <meshStandardMaterial {...MAT.coral} />
      </mesh>
      <group ref={orbit}>
        <mesh position={[1.2, 0, 0.2]} rotation={[Math.PI / 2, 0, 0]}>
          <boxGeometry args={[0.28, 0.04, 0.2]} />
          <meshStandardMaterial {...MAT.pink} />
        </mesh>
      </group>
    </group>
  );
}

function Seo() {
  return (
    <group rotation={[0.15, 0, 0]}>
      {/* bars */}
      {[0.4, 0.7, 1.05].map((h, i) => (
        <mesh key={i} position={[-0.5 + i * 0.5, -0.6 + h / 2, 0]}>
          <boxGeometry args={[0.28, h, 0.28]} />
          <meshStandardMaterial {...(i === 2 ? MAT.accent : MAT.charcoal)} />
        </mesh>
      ))}
      {/* up arrow */}
      <mesh position={[0.5, 0.75, 0]}>
        <coneGeometry args={[0.22, 0.4, 4]} />
        <meshStandardMaterial {...MAT.coral} />
      </mesh>
      {/* magnifier */}
      <group position={[0.35, 0.25, 0.5]} rotation={[0, 0, -0.6]}>
        <mesh>
          <torusGeometry args={[0.34, 0.06, 16, 40]} />
          <meshStandardMaterial {...MAT.chrome} />
        </mesh>
        <mesh position={[0, -0.5, 0]}>
          <cylinderGeometry args={[0.05, 0.05, 0.5, 12]} />
          <meshStandardMaterial {...MAT.chrome} />
        </mesh>
      </group>
    </group>
  );
}

function Funnel() {
  const items = useRef();
  useFrame((s) => {
    if (items.current) items.current.rotation.y = s.clock.elapsedTime * 0.7;
  });
  return (
    <group>
      <mesh position={[0, -0.1, 0]}>
        <cylinderGeometry args={[0.95, 0.22, 1.1, 32, 1, true]} />
        <meshStandardMaterial {...MAT.glass} side={2} />
      </mesh>
      <mesh position={[0, -0.85, 0]}>
        <cylinderGeometry args={[0.22, 0.22, 0.35, 20]} />
        <meshStandardMaterial {...MAT.accent} />
      </mesh>
      <group ref={items} position={[0, 0.6, 0]}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[Math.cos((i / 3) * Math.PI * 2) * 0.5, i * 0.12, Math.sin((i / 3) * Math.PI * 2) * 0.5]}>
            <boxGeometry args={[0.24, 0.3, 0.03]} />
            <meshStandardMaterial {...(i % 2 ? MAT.pink : MAT.chrome)} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

function Social() {
  const orbit = useRef();
  useFrame((s) => {
    if (orbit.current) orbit.current.rotation.y = s.clock.elapsedTime * 0.8;
  });
  return (
    <group>
      {/* phone */}
      <mesh>
        <boxGeometry args={[0.6, 1.05, 0.1]} />
        <meshStandardMaterial {...MAT.charcoal} />
      </mesh>
      <mesh position={[0, 0, 0.06]}>
        <boxGeometry args={[0.5, 0.9, 0.02]} />
        <meshStandardMaterial {...MAT.accent} emissiveIntensity={0.5} />
      </mesh>
      <group ref={orbit}>
        {/* heart-ish */}
        <mesh position={[0.9, 0.4, 0]} rotation={[0, 0, Math.PI / 4]}>
          <boxGeometry args={[0.22, 0.22, 0.22]} />
          <meshStandardMaterial {...MAT.pink} />
        </mesh>
        {/* comment bubble */}
        <mesh position={[-0.9, 0.1, 0.2]}>
          <sphereGeometry args={[0.16, 20, 20]} />
          <meshStandardMaterial {...MAT.coral} />
        </mesh>
        {/* share ring */}
        <mesh position={[0.7, -0.6, -0.2]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.16, 0.05, 12, 30]} />
          <meshStandardMaterial {...MAT.chrome} />
        </mesh>
      </group>
    </group>
  );
}

function Pr() {
  return (
    <group>
      {/* podium */}
      <mesh position={[0, -0.5, 0]}>
        <cylinderGeometry args={[0.5, 0.6, 0.7, 6]} />
        <meshStandardMaterial {...MAT.charcoal} />
      </mesh>
      {/* mic stand */}
      <mesh position={[0, 0.1, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.8, 12]} />
        <meshStandardMaterial {...MAT.chrome} />
      </mesh>
      <mesh position={[0, 0.6, 0]}>
        <sphereGeometry args={[0.2, 24, 24]} />
        <meshStandardMaterial {...MAT.accent} />
      </mesh>
      {/* spotlight beam */}
      <mesh position={[0, 0.5, 0]}>
        <coneGeometry args={[0.9, 1.6, 32, 1, true]} />
        <meshStandardMaterial color={PALETTE.violet} emissive={PALETTE.violet} emissiveIntensity={0.4} transparent opacity={0.12} side={2} />
      </mesh>
    </group>
  );
}

function Web() {
  return (
    <group rotation={[0.15, -0.3, 0]}>
      {[0, 1, 2].map((i) => (
        <group key={i} position={[i * 0.12, i * 0.35 - 0.35, i * -0.12]}>
          <mesh>
            <boxGeometry args={[1.2, 0.8, 0.04]} />
            <meshStandardMaterial {...(i === 2 ? MAT.accent : MAT.glass)} emissiveIntensity={i === 2 ? 0.6 : 0.25} />
          </mesh>
          <mesh position={[-0.45, 0.3, 0.03]}>
            <boxGeometry args={[0.2, 0.08, 0.02]} />
            <meshStandardMaterial {...MAT.chrome} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function Analytics() {
  return (
    <group rotation={[0.2, 0, 0]}>
      <mesh position={[0, -0.5, 0]}>
        <boxGeometry args={[1.5, 0.1, 1.0]} />
        <meshStandardMaterial {...MAT.charcoal} />
      </mesh>
      {[0.4, 0.75, 0.55, 0.9].map((h, i) => (
        <mesh key={i} position={[-0.55 + i * 0.37, -0.45 + h / 2, 0]}>
          <boxGeometry args={[0.22, h, 0.22]} />
          <meshStandardMaterial {...[MAT.accent, MAT.pink, MAT.chrome, MAT.coral][i]} />
        </mesh>
      ))}
      <mesh position={[0.4, 0.7, -0.2]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.22, 0.05, 12, 30]} />
        <meshStandardMaterial {...MAT.chrome} />
      </mesh>
    </group>
  );
}

function Experiential() {
  return (
    <group>
      {/* base */}
      <mesh position={[0, -0.35, 0]}>
        <boxGeometry args={[1.2, 0.12, 0.8]} />
        <meshStandardMaterial {...MAT.charcoal} />
      </mesh>
      {/* lid open */}
      <mesh position={[0, 0.2, -0.4]} rotation={[-1.0, 0, 0]}>
        <boxGeometry args={[1.2, 0.12, 0.8]} />
        <meshStandardMaterial {...MAT.chrome} />
      </mesh>
      {/* glowing stage */}
      <mesh position={[0, -0.1, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial {...MAT.accent} emissiveIntensity={0.9} />
      </mesh>
      <mesh position={[0, 0.25, 0]}>
        <coneGeometry args={[0.18, 0.3, 4]} />
        <meshStandardMaterial {...MAT.coral} />
      </mesh>
    </group>
  );
}

function Partnerships() {
  const g = useRef();
  useFrame((s) => {
    if (g.current) g.current.rotation.y = Math.sin(s.clock.elapsedTime * 0.5) * 0.4;
  });
  return (
    <group ref={g}>
      <mesh position={[-0.35, 0, 0]} rotation={[Math.PI / 2, 0.4, 0]}>
        <torusGeometry args={[0.6, 0.16, 24, 60]} />
        <meshStandardMaterial {...MAT.accent} />
      </mesh>
      <mesh position={[0.35, 0, 0]} rotation={[Math.PI / 2, -0.4, 0]}>
        <torusGeometry args={[0.6, 0.16, 24, 60]} />
        <meshStandardMaterial {...MAT.coral} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.18, 24, 24]} />
        <meshStandardMaterial color="#fff" emissive={PALETTE.violet} emissiveIntensity={1.4} />
      </mesh>
    </group>
  );
}

function Influencer() {
  const sat = useRef();
  useFrame((s) => {
    if (sat.current) sat.current.rotation.z = s.clock.elapsedTime * 0.5;
  });
  return (
    <group rotation={[0.3, 0, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.9, 0.08, 20, 60]} />
        <meshStandardMaterial {...MAT.chrome} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.28, 28, 28]} />
        <meshStandardMaterial {...MAT.accent} />
      </mesh>
      <group ref={sat}>
        {[0, 1, 2].map((i) => {
          const a = (i / 3) * Math.PI * 2;
          const x = Math.cos(a) * 0.9;
          const y = Math.sin(a) * 0.9;
          return (
            <group key={i}>
              <mesh position={[x, y, 0]}>
                <sphereGeometry args={[0.13, 18, 18]} />
                <meshStandardMaterial {...(i % 2 ? MAT.pink : MAT.coral)} />
              </mesh>
              <mesh position={[x / 2, y / 2, 0]} rotation={[0, 0, a - Math.PI / 2]}>
                <cylinderGeometry args={[0.02, 0.02, 0.9, 6]} />
                <meshStandardMaterial color={PALETTE.violet} emissive={PALETTE.violet} emissiveIntensity={0.7} />
              </mesh>
            </group>
          );
        })}
      </group>
    </group>
  );
}

const BUILDERS = {
  gem: Gem,
  easel: Easel,
  target: Target,
  seo: Seo,
  funnel: Funnel,
  social: Social,
  pr: Pr,
  web: Web,
  analytics: Analytics,
  experiential: Experiential,
  partnerships: Partnerships,
  influencer: Influencer,
};

// Idle rotation + cursor-tilt wrapper (shared motion signature).
export default function ServiceModel({ type, pointer }) {
  const group = useRef();
  useHover();
  useFrame((state) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;
    group.current.position.y = Math.sin(t * 0.8) * 0.08;
    const px = pointer?.current?.x ?? 0;
    const py = pointer?.current?.y ?? 0;
    group.current.rotation.y += ((px * 0.6 + t * 0.25) - group.current.rotation.y) * 0.05 + 0.004;
    group.current.rotation.x += (-py * 0.4 - group.current.rotation.x) * 0.05;
  });
  const Builder = BUILDERS[type] || Gem;
  return (
    <group ref={group} scale={0.95}>
      <Builder />
    </group>
  );
}
